import { useState, type ComponentType, type SVGProps } from "react";
import { UploadCloud, ChevronRight, ChevronLeft, FileSpreadsheet, FileText, Image as LucideImage, Pencil } from "lucide-react";
import { toast } from "../../../lib/toast";
import * as XLSX from "xlsx";
import { useOpenAICleaning } from "../../../../imports/useOpenAICleaning";
import { useExcelData } from "../../../../context/ExcelDataContext";

/* ---------------- Icons (kept as-is) ---------------- */

const ErpIcon = (props: SVGProps<SVGSVGElement>) => (
  <svg width="9" height="8" viewBox="0 0 9 8" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M7.82311e-07 1.27916V0.00115561H8.46V1.27916H7.82311e-07ZM7.82311e-07 4.41116V3.13316H8.46V4.41116H7.82311e-07ZM7.82311e-07 7.54316V6.26516H8.46V7.54316H7.82311e-07Z" fill="#492750" />
  </svg>
);

const CsvIcon = (props: SVGProps<SVGSVGElement>) => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M7.82311e-07 11.7719V-6.35386e-05H11.772V11.7719H7.82311e-07ZM1.278 5.25594H5.238V1.27794H1.278V5.25594ZM6.516 5.25594H10.494V1.27794H6.516V5.25594ZM1.278 10.4939H5.238V6.53394H1.278V10.4939ZM6.516 10.4939H10.494V6.53394H6.516V10.4939Z" fill="#492750" />
  </svg>
);

const ImageIcon = (props: SVGProps<SVGSVGElement>) => (
  <LucideImage width={props.width} height={props.height} color="#492750" strokeWidth={1.8} />
);

const ApiIcon = (props: SVGProps<SVGSVGElement>) => (
  <svg width="19" height="19" viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M9.072 18.1278L7.82311e-07 9.07384L9.072 0.00184274L18.126 9.07384L9.072 18.1278ZM9.072 16.4898L16.488 9.07384L9.072 1.63984L1.656 9.07384L9.072 16.4898ZM9.072 12.8358L5.292 9.07384L9.072 5.29384L12.852 9.07384L9.072 12.8358Z" fill="#492750" />
  </svg>
);

const P2pIcon = (props: SVGProps<SVGSVGElement>) => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6.606 13.2114C2.952 13.2114 7.82311e-07 10.2594 7.82311e-07 6.60537C7.82311e-07 2.95137 2.952 -0.00062573 6.606 -0.00062573C10.26 -0.00062573 13.212 2.95137 13.212 6.60537C13.212 10.2594 10.224 13.2114 6.606 13.2114ZM6.048 1.33137C3.564 1.58337 1.602 3.54537 1.332 6.02937H6.048V1.33137ZM7.2 1.33137V6.01137H11.88C11.61 3.54537 9.666 1.60137 7.2 1.33137ZM7.182 7.16337V11.8794C9.666 11.6094 11.628 9.64737 11.88 7.16337H7.182ZM1.332 7.18137C1.602 9.64737 3.564 11.6274 6.03 11.8794V7.18137H1.332Z" fill="#492750" />
  </svg>
);

const BankIcon = (props: SVGProps<SVGSVGElement>) => (
  <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M7.128 14.2565C6.144 14.2565 5.22 14.0705 4.356 13.6985C3.492 13.3385 2.73 12.8345 2.07 12.1865C1.422 11.5265 0.912001 10.7645 0.540001 9.90053C0.180001 9.03653 7.82311e-07 8.11253 7.82311e-07 7.12853C7.82311e-07 6.13253 0.180001 5.20853 0.540001 4.35653C0.912001 3.49253 1.422 2.73653 2.07 2.08853C2.73 1.42853 3.492 0.918531 4.356 0.55853C5.22 0.186531 6.144 0.000530958 7.128 0.000530958C8.124 0.000530958 9.048 0.186531 9.9 0.55853C10.764 0.918531 11.52 1.42853 12.168 2.08853C12.828 2.73653 13.338 3.49253 13.698 4.35653C14.07 5.20853 14.256 6.13253 14.256 7.12853C14.256 8.11253 14.07 9.03653 13.698 9.90053C13.338 10.7645 12.828 11.5265 12.168 12.1865C11.52 12.8345 10.764 13.3385 9.9 13.6985C9.048 14.0705 8.124 14.2565 7.128 14.2565ZM7.128 13.5005C8.016 13.5005 8.844 13.3385 9.612 13.0145C10.38 12.6785 11.052 12.2225 11.628 11.6465C12.216 11.0585 12.672 10.3805 12.996 9.61253C13.332 8.84453 13.5 8.01653 13.5 7.12853C13.5 6.24053 13.332 5.41253 12.996 4.64453C12.672 3.87653 12.216 3.20453 11.628 2.62853C11.052 2.04053 10.38 1.58453 9.612 1.26053C8.844 0.924531 8.016 0.756531 7.128 0.756531V13.5005Z" fill="#492750" />
  </svg>
);

const CloudIcon = (props: SVGProps<SVGSVGElement>) => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6.75639 13.5014C5.82741 13.5014 4.95384 13.3267 4.13565 12.9773C3.32173 12.6278 2.60369 12.1442 1.98153 11.5263C1.36364 10.9041 0.877841 10.1861 0.524148 9.37216C0.174716 8.55824 0.0021307 7.68466 0.00639211 6.75142C0.0106534 5.81818 0.1875 4.9446 0.536932 4.13068C0.890625 3.31676 1.37642 2.60085 1.99432 1.98295C2.61222 1.3608 3.32813 0.875 4.14205 0.525568C4.95597 0.176136 5.82741 0.00142026 6.75639 0.00142026C7.68963 0.00142026 8.56321 0.176136 9.37713 0.525568C10.1953 0.875 10.9112 1.3608 11.5249 1.98295C12.1428 2.60085 12.6264 3.31676 12.9759 4.13068C13.3253 4.9446 13.5021 5.81818 13.5064 6.75142C13.5107 7.68466 13.3381 8.55824 12.9886 9.37216C12.6392 10.1861 12.1555 10.9041 11.5376 11.5263C10.9197 12.1442 10.2017 12.6278 9.38352 12.9773C8.56534 13.3267 7.68963 13.5014 6.75639 13.5014ZM6.75639 11.5902C7.42543 11.5902 8.05398 11.4645 8.64205 11.2131C9.23011 10.9616 9.74361 10.6165 10.1825 10.1776C10.6257 9.73437 10.9709 9.22088 11.218 8.63707C11.4695 8.04901 11.5952 7.42259 11.5952 6.75781C11.5909 6.08878 11.4631 5.46023 11.2116 4.87216C10.9645 4.28409 10.6193 3.7706 10.1761 3.33168C9.73296 2.88849 9.21946 2.54119 8.63565 2.28977C8.05185 2.03835 7.42543 1.91264 6.75639 1.91264C6.09162 1.91264 5.46733 2.03835 4.88352 2.28977C4.29972 2.54119 3.78622 2.88849 3.34304 3.33168C2.89986 3.77486 2.55256 4.29048 2.30114 4.87855C2.04972 5.46236 1.92188 6.08878 1.91761 6.75781C1.91761 7.41832 2.04119 8.04261 2.28835 8.63068C2.53977 9.21449 2.88707 9.72798 3.33026 10.1712C3.77344 10.6143 4.28693 10.9616 4.87074 11.2131C5.45881 11.4645 6.08736 11.5902 6.75639 11.5902Z" fill="#492750" />
  </svg>
);

/* ---------------- Types ---------------- */

type SourceStatus = "connected" | "pending";
type UploadCardId = "csv" | "excel" | "invoices" | "image";

type DataSource = {
  id: string;
  label: string;
  sub: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  color: string;
  status: SourceStatus;
};

type DataIngestionStepProps = {
  onNext: (data: any[], rawInput: any[]) => void;
  onBack: () => void;
  onCancel: () => void;
};

/* ---------------- Utilities to prepare SimplAI-ready data ---------------- */

// convert header into SimplAI-safe underscore header
const toSimplAIHeader = (header: string) => {
  return String(header || "")
    .trim()
    .replace(/[()]/g, "")
    .replace(/&/g, "and")
    .replace(/[\/\\-]+/g, " ")
    .replace(/[^\w\s]/g, "")
    .replace(/\s+/g, "_")
    .replace(/^_+|_+$/g, "");
};

// convert rows with human headers to rows with underscore headers for SimplAI
const convertRowsToSimplAIFormat = (rows: Record<string, unknown>[]) => {
  return rows.map((row) => {
    const converted: Record<string, unknown> = {};

    Object.entries(row || {}).forEach(([key, value]) => {
      if (key.startsWith("__") || key === "run_id") {
        return;
      }

      converted[toSimplAIHeader(key)] = value;
    });

    return converted;
  });
};

/* ---------------- Constants & helper functions (kept from your file) ---------------- */

const DATA_SOURCES: DataSource[] = [
  { id: "erp", label: "ERP System", sub: "SAP, Oracle, MS Dynamics", icon: ErpIcon, color: "#F8F3FA", status: "connected" },
  { id: "image", label: "Image Upload", sub: "PNG, JPG, scanned documents", icon: ImageIcon, color: "#F8F3FA", status: "connected" },
  { id: "api", label: "API Connection", sub: "REST / SFTP integration", icon: ApiIcon, color: "#F8F3FA", status: "connected" },
  { id: "p2p", label: "P2P Platform", sub: "Coupa, Jaggaer, Ariba", icon: P2pIcon, color: "#F8F3FA", status: "connected" },
  { id: "bank", label: "Bank Feeds", sub: "Direct bank statement sync", icon: BankIcon, color: "#F8F3FA", status: "pending" },
  { id: "cloud", label: "Cloud Storage", sub: "S3, SharePoint, Google Drive", icon: CloudIcon, color: "#F8F3FA", status: "pending" },
];

const SCHEMA_PREVIEW = [
  ["transaction_id", "string", "TX-104821"],
  ["supplier_name", "string", "Acme Logistic"],
  ["invoice_date", "date", "2026-02-14"],
  ["amount", "number", "48,210.00"],
  ["currency", "enum", "USD"],
];

const toCellString =(value: unknown) => {
  if (value === null || value === undefined) {
    return "";
  }
  return typeof value === "string" ? value : String(value);
};

type SchemaConfigRow = { task_name?: string; task_desc?: string } & Record<string, unknown>;
type SchemaDefinition = {
  type: "json";
  json_schema: {
    type: "object";
    properties: Record<string, { type: "string"; description: string }>;
    required: string[];
    additionalProperties: boolean;
    description: string;
  };
};

/* ---------------- Component ---------------- */

export function DataIngestionStep({ onNext, onBack, onCancel }: DataIngestionStepProps) {
  const { runOpenAICleaning, loading: isProcessing } = useOpenAICleaning();

  // UPDATED: expose more setters in context
  const {
    setExcelData,
    setRawExcelData,
    setCleanedExcelData,
    setSimplAIReadyData,
  } = useExcelData();

  const [selectedSources, setSelectedSources] = useState<string[]>([]);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [fileName, setFileName] = useState("");
  const [uploadNames, setUploadNames] = useState<Record<UploadCardId, string>>({
    csv: "",
    excel: "",
    invoices: "",
    image: "",
  });
  const [configured, setConfigured] = useState<string[]>([]);

  const toggleSource = (id: string) =>
    setSelectedSources((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const configure = (id: string) => {
    toast.success(`${DATA_SOURCES.find((s) => s.id === id)?.label} configured successfully`);
    setConfigured((c) => [...c, id]);
  };

  const handleUploadSelection = (cardId: UploadCardId, file: File) => {
    setUploadNames((current) => ({ ...current, [cardId]: file.name }));
    if (cardId !== "invoices" && cardId !== "image") {
      setUploadedFile(file);
      setFileName(file.name);
    }
    toast.success(`Uploaded: ${file.name}`);
  };

  type StatCardProps = { title: string; value: string | number };
  const StatCard = ({ title, value }: StatCardProps) => (
    <div className="stat-card">
      <div className="stat-title">{title}</div>
      <div className="stat-value">{value}</div>
    </div>
  );

  type ProgressProps = { label: string; value: number; warning?: boolean };
  const Progress = ({ label, value, warning }: ProgressProps) => (
    <div className="progress-item">
      <div className="progress-label">
        {label}
        <span>{value}%</span>
      </div>
      <div className="progress-bar">
        <div className={`fill ${warning ? "warning" : ""}`} style={{ width: `${value}%` }} />
      </div>
    </div>
  );

  // Schema builder (kept, optional)
  const buildSchemaFromConfig = (rows: SchemaConfigRow[], descriptionFallback: string): SchemaDefinition => {
    const properties: Record<string, { type: "string"; description: string }> = {};
    const required: string[] = [];
    let description = "";
    const seen = new Set();
    rows.forEach((row: SchemaConfigRow) => {
      const name = (row.task_name || "").trim();
      const desc = (row.task_desc || "").trim();
      if (!name) return;
      if (name.toLowerCase() === "task_description") {
        description = desc;
        return;
      }
      if (!seen.has(name)) {
        properties[name] = { type: "string", description: desc || name };
        required.push(name);
        seen.add(name);
      }
    });
    return {
      type: "json",
      json_schema: { type: "object", properties, required, additionalProperties: false, description: description || descriptionFallback },
    };
  };

  // MAIN: handle data cleaning (reads real headers and sends rows directly)
  const handleDataCleaning = () => {
    if (!uploadedFile) {
      toast.error("Upload file first");
      return;
    }

    const reader = new FileReader();

    reader.onload = async (e: ProgressEvent<FileReader>) => {
      try {
        const result = e.target?.result;
        if (typeof result !== "string") {
          toast.error("Unable to read the uploaded file");
          return;
        }

        const workbook = XLSX.read(result, { type: "binary" });
        const sheetNames = workbook.SheetNames || [];

        if (sheetNames.length === 0) {
          toast.error("No sheets found in uploaded Excel file");
          return;
        }

        const getSheet = (name: string) => {
          const key = Object.keys(workbook.Sheets).find((k) => k.toLowerCase() === name.toLowerCase());
          return key ? workbook.Sheets[key] : null;
        };

        // Prefer sheet named "input", otherwise use first sheet
        const inputSheet = getSheet("input") || workbook.Sheets[sheetNames[0]];

        if (!inputSheet) {
          toast.error("Unable to detect input sheet");
          return;
        }

        const inputData = XLSX.utils.sheet_to_json<Record<string, unknown>>(inputSheet, { defval: "" });

        if (!Array.isArray(inputData) || inputData.length === 0) {
          toast.error("No data rows found in uploaded Excel file");
          return;
        }

        console.log("✅ RAW EXCEL DATA WITH REAL HEADERS:", inputData);

        const detectedHeaders = Object.keys(inputData[0] || {});
        if (detectedHeaders.length === 0) {
          toast.error("No headers detected in uploaded Excel file");
          return;
        }
        console.log("✅ DETECTED HEADERS:", detectedHeaders);

        let cleanedRows: Record<string, unknown>[] = [];

        try {
          const openAIResponse = await runOpenAICleaning(inputData);

          console.log("✅ OPENAI CLEANING RESPONSE:", openAIResponse);

          cleanedRows = Array.isArray(openAIResponse?.rows)
            ? openAIResponse.rows.map((row: Record<string, unknown>, index: number) => ({
                __sourceRow: index + 1,
                run_id: `openai-${index + 1}`,
                __runStatus: "completed",
                __warningCount: 0,
                __warningMessage: "",
                ...row,
              }))
            : [];
        } catch (err) {
          console.error("❌ OpenAI cleaning failed:", err);
          toast.error("OpenAI cleaning failed");
          return;
        }

        if (!cleanedRows || cleanedRows.length === 0) {
          toast.error("OpenAI returned no cleaned rows");
          return;
        }

        // Prepare display and SimplAI-ready data, and store raw/cleaned in context
        const cleanedRowsForDisplay = cleanedRows;
        const cleanedRowsForSimplAI = convertRowsToSimplAIFormat(cleanedRowsForDisplay);

        console.log("✅ CLEANED ROWS FOR DISPLAY:", cleanedRowsForDisplay);
        console.log("✅ CLEANED ROWS FOR SIMPLAI:", cleanedRowsForSimplAI);

        // Store raw uploaded data
       // if (typeof setRawExcelData === "function") setRawExcelData(inputData as any);

        // Store cleaned data with readable headers
       // if (typeof setCleanedExcelData === "function") setCleanedExcelData(cleanedRowsForDisplay as any);

        // Backward compatibility for existing screens
       // if (typeof setExcelData === "function") setExcelData(cleanedRowsForDisplay as any);

        // Store cleaned data with underscore headers for SimplAI agents
       // if (typeof setSimplAIReadyData === "function") setSimplAIReadyData(cleanedRowsForSimplAI as any);

        toast.success("Processing complete");

        // Send cleaned rows to next step; also send raw input for traceability
        onNext(cleanedRowsForDisplay, inputData);
      } catch (err) {
        console.error("❌ Processing failed:", err);
        toast.error("Processing failed");
      }
    };

    reader.readAsBinaryString(uploadedFile);
  };

  /* ---------------- UI (unchanged except behavior) ---------------- */

  return (
    <div className="setup-screen">
      {isProcessing && (
        <div className="ai-processing-overlay">
          <div className="loader_pulse"><div></div><div></div><div></div><div></div><div></div></div>
          <div className="ai-processing-overlay__label">AI is processing your data, please wait...</div>
        </div>
      )}
      <div className="top-stats">
        <StatCard title="NUMBER OF ROWS" value="4,218,402" />
        <StatCard title="TOTAL SPEND" value="$2.84B" />
        <StatCard title="TOTAL SUPPLIERS" value="12,481" />
        <StatCard title="PIPELINE LATENCY" value="2m 14s" />
      </div>

      <div className="main-grid">
        <div className="left-panel">
          <div className="upload-cards-wrap upload-cards-wrap--top">
            <div className="upload-card" onClick={() => document.getElementById("csvFileInput")?.click()}>
              <UploadCloud size={25} color="#492750" strokeWidth={1.8} />
              <div className="upload-title">CSV upload</div>
              <div className="sub">{uploadNames.csv || "Drag or browse"}</div>
            </div>

            <div className="upload-card" onClick={() => document.getElementById("excelFileInput")?.click()}>
              <FileSpreadsheet size={24} color="#492750" strokeWidth={1.8} />
              <div className="upload-title">Excel upload</div>
              <div className="sub">{uploadNames.excel || ".xlsx .xls"}</div>
            </div>

            <div className="upload-card" onClick={() => document.getElementById("invoiceFileInput")?.click()}>
              <FileText size={24} color="#492750" strokeWidth={1.8} />
              <div className="upload-title">Invoices / SOWs</div>
              <div className="sub">{uploadNames.invoices || "OCR processing"}</div>
            </div>
          </div>

          {/* HEADER */}
          <div className="section-header">
            <div>
              <div className="connectorTitle">Connectors & Sources</div>
              <div className="sub">ERP, P2P, CLM, OCR, and file-based ingestion.</div>
            </div>

            <button className="btn btn--primary add-connector-btn">Add Connectors</button>
          </div>

          {/* CONNECTOR CARDS */}
          <div className="connector-grid">
            {DATA_SOURCES.map(({ id, label, sub, icon: Icon, color, status }) => {
              const done = configured.includes(id);
              const currentStatus = done ? "connected" : status;
              return (
                <div key={id} className={`connector-card ${selectedSources.includes(id) ? "active" : ""}`} onClick={() => { toggleSource(id); if (id === "image") document.getElementById("imageFileInput")?.click(); }}>
                  <div className="icon" style={{ background: `${color}` }}><Icon width={18} height={18} /></div>
                  <div className="content">
                    <div className="label">{label}</div>
                    <div className="sub">{sub}</div>
                  </div>
                  <div className={`status ${currentStatus === "connected" ? "success" : "pending"}`}>{currentStatus === "connected" ? "Connected" : "Auth required"}</div>
                </div>
              );
            })}
          </div>

          <input id="csvFileInput" type="file" accept=".csv" style={{ display: "none" }} onChange={(e) => { const file = e.target.files?.[0]; if (file) handleUploadSelection("csv", file); }} />
          <input id="excelFileInput" type="file" accept=".xlsx,.xls" style={{ display: "none" }} onChange={(e) => { const file = e.target.files?.[0]; if (file) handleUploadSelection("excel", file); }} />
          <input id="imageFileInput" type="file" accept="image/*" style={{ display: "none" }} onChange={(e) => { const file = e.target.files?.[0]; if (file) handleUploadSelection("image", file); }} />
          <input id="invoiceFileInput" type="file" accept=".pdf,.png,.jpg,.jpeg,.doc,.docx" style={{ display: "none" }} onChange={(e) => { const file = e.target.files?.[0]; if (file) handleUploadSelection("invoices", file); }} />
        </div>

        <div className="right-panel">
          {/* HEADER */}
          <div className="section-header">
            <div>
              <div className="title">File structure preview</div>
              <div className="sub">Detected schema from latest batch.</div>
            </div>
            <button className="btn btn--primary add-connector-btn">Add Column</button>
          </div>

          <div className="preview-table-wrap">
            <table className="preview-table">
              <thead>
                <tr><th>COLUMN</th><th>TYPE</th><th>SAMPLE</th><th className="preview-table__action-col">EDIT</th></tr>
              </thead>
              <tbody>
                {SCHEMA_PREVIEW.map(([column, type, sample]) => (
                  <tr key={column}>
                    <td>{column}</td>
                    <td>{type}</td>
                    <td>{sample}</td>
                    <td className="preview-table__action-col">
                      <button type="button" className="preview-table__edit" aria-label={`Edit ${column}`}>
                        <Pencil size={13} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="progress-block">
            <Progress label="Schema parse success" value={99.7} />
            <Progress label="Records validated" value={94.2} />
            <Progress label="Duplicates flagged" value={12.4} warning />
          </div>
        </div>
      </div>

      <div className="wizard-action-bar">
        <button className="action-bar__cancel btn btn--outline" onClick={onCancel}>Cancel</button>
        <button className="btn btn--outline m-left-auto" onClick={onBack}><ChevronLeft size={14} /> Back</button>
        <button className="btn btn--primary" onClick={handleDataCleaning} disabled={isProcessing}>
          {isProcessing ? "Processing" : "Data Cleaning"} <ChevronRight size={14} />
        </button>
      </div>
    </div>
  );
}

export default DataIngestionStep;